// src/pages/MergerDashboard/AllianceTechnologyTab.js

import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid
} from 'recharts';

const apiUrl = API_URL;
const barPalette = ['#2b4f72', '#4f7ea7', '#2f8f83', '#b2692b', '#8b4ea3', '#4f5b66', '#8b3a3a'];

const AllianceTechnologyTab = ({ nationA, nationB }) => {
    // States
    const [isLoading, setIsLoading] = useState(false);
    const [topAlliances, setTopAlliances] = useState([]); // top 5 alliances
    const [barData, setBarData] = useState([]);           // for grouped bar chart

    // On mount or whenever nationA/nationB changes, fetch the data
    useEffect(() => {
        if (!nationA) return;

        const fetchAllianceTechnology = async () => {
            setIsLoading(true);
            try {
                // GET /merger/alliance-technology?nationA=xxx&nationB=yyy
                const response = await axiosInstance.get(`${apiUrl}/merger/alliance-technology`, {
                    params: nationB ? { nationA, nationB } : { nationA }
                });
                // { topAlliances, barData, allAlliances }
                setTopAlliances(response.data.topAlliances || []);
                setBarData(response.data.barData || []);
            } catch (error) {
                console.error('Error fetching alliance technology summary:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllianceTechnology();
    }, [nationA, nationB]);

    // 
    // 1) Build "grouped bar" data for Recharts
    //
    // The endpoint gave us barData like:
    //   [
    //     { acronym: "ABC", production: 30, war: 20, other: 10 },
    //     { acronym: "XYZ", production: 15, war: 25, other: 5 },
    //     ...
    //   ]
    //
    // However, to group by category (X axis = production/war/other),
    // we transform it to something like:
    //   [
    //     { category: "production", ABC: 30, XYZ: 15, ... },
    //     { category: "war",        ABC: 20, XYZ: 25, ... },
    //     { category: "other",      ABC: 10, XYZ: 5,  ... },
    //   ]
    //
    const groupedBarData = useMemo(() => {
        if (!barData.length) return [];

        // For each category, build an object: { category, [acronym1]: number, [acronym2]: number, ... }
        const categories = ['production', 'war', 'other'];
        const result = categories.map((cat) => {
            const row = { category: cat };
            // For each alliance in barData
            barData.forEach((ally) => {
                // e.g. row["ABC"] = ally.production
                row[ally.acronym] = ally[cat] || 0;
            });
            return row;
        });

        return result;
    }, [barData]);

    //
    // 2) Table with categories across rows, alliances across columns
    // The topAlliances array has each alliance.sums + alliance.technology
    // We want to show a "category row" with sums for each alliance, then each sub-tech row.
    //

    // The known sub-tech structure
    const productionKeys = [
        'foodProduction',
        'oilProduction',
        'steelProduction',
        'mineralProduction',
        'foodGather',
        'oilGather',
        'steelGather',
        'mineralGather',
        'civilEngineering',
        'researchFocus'
    ];
    const warKeys = [
        'meleeHp',
        'midRangeHp',
        'longRangeHp',
        'meleeDefence',
        'midRangeDefence',
        'longRangeDefence',
        'meleeAttack',
        'midRangeAttack',
        'longRangeAttack'
    ];
    const otherKeys = [
        'allianceAssistance',
        'rallyOrganization',
        'teamExpansion',
        'taxCollection',
        'helpTime',
        'plantFlags'
    ];

    // Helper to safely read the level for a sub-tech
    const getTechLevel = (alliance, category, techKey) => {
        // alliance.technology?.production?.foodProduction?.level
        return alliance?.technology?.[category]?.[techKey]?.level || 0;
    };

    return (
        <div className="md-tech-tab">
            <h2>Alliance Technology Summary</h2>
            {isLoading && <p>Loading alliance technology data...</p>}

            {/* 1) Grouped Bar Chart with categories on X-axis, alliances as multiple bars */}
            {!isLoading && groupedBarData.length > 0 && (
                <div className="md-chart-wrap">
                    <ResponsiveContainer>
                        <BarChart data={groupedBarData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            {/* For each alliance acronym, we add a <Bar /> */}
                            {barData.map((ally, index) => (
                                <Bar
                                    key={ally.acronym}
                                    dataKey={ally.acronym}
                                    name={ally.acronym}
                                    fill={barPalette[index % barPalette.length]}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* 2) Detailed Table of top 5 alliances */}
            {!isLoading && topAlliances.length > 0 && (
                <div className="md-table-wrap">
                    <h3>Top 5 Alliances Detailed Table</h3>
                    <table className="md-tech-summary-table">
                        <thead>
                            <tr>
                                <th>Technology</th>
                                {topAlliances.map((ally) => (
                                    <th key={ally._id}>
                                        {ally.acronym}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="md-tech-highlight-row">
                                <td>Grand Total</td>
                                {topAlliances.map((ally) => (
                                    <td key={ally._id}>
                                        {ally.sums.grandTotal}
                                    </td>
                                ))}
                            </tr>
                            {/* Production category row */}
                            <tr className="md-tech-category-row">
                                <td>Production</td>
                                {topAlliances.map((ally) => (
                                    <td key={ally._id}>
                                        {ally.sums.production}
                                    </td>
                                ))}
                            </tr>
                            {/* Each sub-tech row under production */}
                            {productionKeys.map((techKey) => (
                                <tr key={techKey}>
                                    <td>{techKey}</td>
                                    {topAlliances.map((ally) => (
                                        <td key={ally._id}>
                                            {getTechLevel(ally, 'production', techKey)}
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            {/* War category row */}
                            <tr className="md-tech-category-row">
                                <td>War</td>
                                {topAlliances.map((ally) => (
                                    <td key={ally._id}>
                                        {ally.sums.war}
                                    </td>
                                ))}
                            </tr>
                            {/* Each sub-tech row under war */}
                            {warKeys.map((techKey) => (
                                <tr key={techKey}>
                                    <td>{techKey}</td>
                                    {topAlliances.map((ally) => (
                                        <td key={ally._id}>
                                            {getTechLevel(ally, 'war', techKey)}
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            {/* Other category row */}
                            <tr className="md-tech-category-row">
                                <td>Other</td>
                                {topAlliances.map((ally) => (
                                    <td key={ally._id}>
                                        {ally.sums.other}
                                    </td>
                                ))}
                            </tr>
                            {/* Each sub-tech row under other */}
                            {otherKeys.map((techKey) => (
                                <tr key={techKey}>
                                    <td>{techKey}</td>
                                    {topAlliances.map((ally) => (
                                        <td key={ally._id}>
                                            {getTechLevel(ally, 'other', techKey)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AllianceTechnologyTab;
